import * as THREE from 'three'
import type { Sluff } from '../physics/sluff'
import { radialTexture } from './textures'
import { HAZE } from './atmosphere'
import { SPRAY_SUN, SPRAY_SKY } from './spray'

/**
 * Sluff, drawn as a moving field of loose snow behind the skier.
 *
 * It has to read instantly and from behind: the whole mechanic is knowing
 * whether the snow you released is about to catch you (spec §6), and a plume
 * you cannot see is a hazard you cannot manage. Bigger, brighter particles than
 * the ski spray, and they sit on the surface rather than hanging in the air.
 */
export class SluffMesh {
  readonly points: THREE.Points
  private readonly pos: Float32Array
  private readonly size: Float32Array
  private readonly alpha: Float32Array
  private readonly geom = new THREE.BufferGeometry()

  constructor(private readonly sluff: Sluff) {
    const n = sluff.capacity
    this.pos = new Float32Array(n * 3)
    this.size = new Float32Array(n)
    this.alpha = new Float32Array(n)
    for (let i = 0; i < n; i++) this.pos[i * 3 + 1] = -10000

    this.geom.setAttribute('position', new THREE.BufferAttribute(this.pos, 3))
    this.geom.setAttribute('size', new THREE.BufferAttribute(this.size, 1))
    this.geom.setAttribute('alpha', new THREE.BufferAttribute(this.alpha, 1))
    this.geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6)

    const mat = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: radialTexture(48, 0.42) },
        uSunDir: HAZE.sunDir,
        uSunCol: SPRAY_SUN,
        uSkyCol: SPRAY_SKY,
      },
      vertexShader: `
        attribute float size;
        attribute float alpha;
        varying float vAlpha;
        void main() {
          vAlpha = alpha;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = size * (340.0 / max(-mv.z, 1.0));
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: `
        uniform sampler2D map;
        uniform vec3 uSunDir;
        uniform vec3 uSunCol;
        uniform vec3 uSkyCol;
        varying float vAlpha;
        void main() {
          vec4 t = texture2D(map, gl_PointCoord);
          vec2 d = gl_PointCoord * 2.0 - 1.0;
          float r2 = dot(d, d);
          if (r2 > 1.0) discard;
          vec3 n = normalize(vec3(d, sqrt(max(0.0, 1.0 - r2))));
          float lambert = max(dot(n, uSunDir), 0.0);
          // Slightly brighter than ski spray: a sluff is a moving mass of snow
          // catching light across its whole face, not a thrown plume.
          vec3 lit = uSkyCol * 1.15 + uSunCol * (0.45 + 0.55 * lambert);
          gl_FragColor = vec4(lit, t.a * vAlpha);
          if (gl_FragColor.a < 0.01) discard;
        }`,
      transparent: true,
      depthWrite: false,
    })

    this.points = new THREE.Points(this.geom, mat)
    this.points.frustumCulled = false
    this.points.renderOrder = 2
  }

  update(): void {
    const { positionsX, positionsY, positionsZ, masses, capacity } = this.sluff
    for (let i = 0; i < capacity; i++) {
      const m = masses[i]
      const o = i * 3
      if (m <= 0) {
        this.pos[o + 1] = -10000
        this.alpha[i] = 0
        continue
      }
      this.pos[o] = positionsX[i]
      // Sits on the snow, riding just above the surface.
      this.pos[o + 1] = positionsY[i] + 0.35 + m * 0.5
      this.pos[o + 2] = positionsZ[i]
      this.size[i] = 1.6 + m * 2.6
      this.alpha[i] = Math.min(0.85, 0.35 + m * 0.45)
    }
    this.geom.attributes.position.needsUpdate = true
    this.geom.attributes.size.needsUpdate = true
    this.geom.attributes.alpha.needsUpdate = true
  }

  clear(): void {
    for (let i = 0; i < this.sluff.capacity; i++) {
      this.pos[i * 3 + 1] = -10000
      this.alpha[i] = 0
    }
    this.geom.attributes.position.needsUpdate = true
    this.geom.attributes.alpha.needsUpdate = true
  }
}
